"use client";

import { useEffect, useLayoutEffect } from "react";
import { usePathname } from "next/navigation";
import {
  normalizePathTrailingSlashes,
  pathWithoutLocale,
} from "@/lib/localePath";
import { useNavigationPending } from "./NavigationPendingContext";
import { skeletonForRoute } from "@/components/skeletons/routeSkeleton";

/**
 * Full-area overlay on the main column while a sidebar / menu Link navigation is in flight.
 * Complements `loading.tsx` (which often does not run when RSC is prefetched + cached).
 */
export default function MainContentPendingOverlay() {
  const { navigationTarget, setPendingFromLink, clearNavigationPending } =
    useNavigationPending();
  const pathname = usePathname();
  const current = normalizePathTrailingSlashes(pathWithoutLocale(pathname));

  // Last resort if RSC never commits (stalled navigation)
  useEffect(() => {
    if (!navigationTarget) return;
    const id = window.setTimeout(() => clearNavigationPending(), 15_000);
    return () => window.clearTimeout(id);
  }, [navigationTarget, clearNavigationPending]);

  // Clear overlay in the same frame the URL matches — avoids a flash of stale chrome
  useLayoutEffect(() => {
    if (!navigationTarget) return;
    const target = normalizePathTrailingSlashes(navigationTarget);
    const atHome = target === "/" && (current === "/" || current === "");
    const atTarget =
      current === target || current.startsWith(`${target}/`);
    if (atHome || atTarget) {
      setPendingFromLink(navigationTarget, false);
    }
  }, [current, navigationTarget, setPendingFromLink]);

  if (!navigationTarget) return null;

  return (
    <div
      className="absolute inset-0 z-30 bg-gray-100 overflow-y-auto"
      aria-busy
      aria-label="Loading page"
    >
      {skeletonForRoute(navigationTarget)}
    </div>
  );
}
